/**
 * leadAttribution.ts — does this lead COUNT? The browser's copy.
 *
 * 🔴 WHY A SECOND COPY EXISTS, stated plainly rather than hidden. The canonical
 * implementation is `backend/functions/src/leads/leadAttribution.ts` (+
 * `agents/leadExclusion.ts`), built 2026-09-11 and wired into five Ava-facing
 * surfaces. The backend cannot import from `app/shared` — `utils/clientFiltering.ts`
 * already mirrors `app/shared/src/realClient.ts` for exactly this reason — so the
 * choice was a mirror or a round trip, and a count the dashboard needs on every
 * render should not be a network call.
 *
 * What makes the mirror safe is `__fixtures__/leadAttributionCases.json`: a truth
 * table both sides run. A case that disagrees fails on whichever side is wrong.
 * That is one BEHAVIOUR with two implementations, which is honest; two
 * implementations and a hope is what this replaces.
 *
 * 🔴 WHAT IT FIXES. `useClientData` counted rows — `leads.length` and a by-source
 * tally — and applied NONE of the identity axis. Measured 2026-09-12: the backend
 * knew Jake had ZERO real leads while his dashboard showed 2, because both of his
 * "leads" were his own mobile and our platform number. Two rulers, one client, and
 * whichever number he saw last was the one he believed.
 */

export type LeadExclusionReason =
  | 'test-contact'
  | 'owner-own-number'
  | 'platform-number'
  | 'client-provisioned-number'
  | 'owner-own-email'
  | 'owner-own-name'
  | 'client-own-domain'
  | 'automated-sender'
  | 'owner-test-number'
  | 'outbound-first'
  | 'email-not-new-inquiry'
  | 'solicitation';

/** Sources we can honestly claim credit for. Mirrors ARRIVED_LEAD_SOURCES. */
export const AD_LEAD_SOURCES = ['facebook-ad', 'instagram-ad', 'google-lsa'] as const;
export const PROSPECTING_LEAD_SOURCES = ['prospecting', 'prospecting-referral', 'prospecting-form'] as const;
export const ARRIVED_LEAD_SOURCES = [
  'phone', 'sms', 'chat', 'email', 'web-form', 'website',
  // 'web' — the server added it 2026-09-24 (Lead Memory stamps source = channel,
  // and every website writer records 'web'); this mirror lagged until 2026-09-26,
  // so every website lead was missing from the Control Center dashboard count.
  'web',
  ...AD_LEAD_SOURCES,
  // Mirrors the server (2026-10-21): a reply to the client's PROSPECTING outreach.
  ...PROSPECTING_LEAD_SOURCES,
  // Mirrors the server (2026-10-26): a person a SUPPLIER handed over by email.
  'supplier-referral',
] as const;

export interface LeadExclusionClient {
  phone?: unknown;
  ownerPhone?: unknown;
  email?: unknown;
  ownerEmail?: unknown;
  avaPhoneNumber?: unknown;
  businessPhoneNumber?: unknown;
  baseTexting?: { phoneNumber?: unknown } | null;
  businessEmail?: unknown;
  googleWorkspaceEmail?: unknown;
  /** Connected-account addresses — attached by the server; absent in the browser unless supplied. */
  connectedEmails?: unknown;
  /** Numbers the owner gave while testing their own assistant (server: agents/leadExclusion.ts). */
  ownerTestNumbers?: unknown;
  ownerName?: unknown;
  ownerFirstName?: unknown;
  ownerLastName?: unknown;
  website?: unknown;
  domainName?: unknown;
  ownedDomain?: unknown;
}

export interface LeadRowForAttribution {
  source?: unknown;
  phone?: unknown;
  phoneNumber?: unknown;
  email?: unknown;
  name?: unknown;
  interactions?: unknown;
  /** Mirrors the server (2026-10-03): set on a person SELLING to the business. */
  notACustomer?: unknown;
}

/**
 * PURE — the WHAT-THEY-WANT axis. Mirrors the server's isMarkedNotACustomer +
 * firstInteractionIsSolicitationFree (leads/solicitation.ts, 2026-10-03): a funding /
 * SEO / vendor pitch to the business is never a lead. Austin's "1 new lead" was one.
 */
export function isSolicitationRow(lead: { notACustomer?: unknown; interactions?: unknown }): boolean {
  const m = lead.notACustomer as { reason?: unknown } | null | undefined
  if (m && typeof m === 'object' && typeof m.reason === 'string' && m.reason.length > 0) return true
  const first = Array.isArray(lead.interactions) && lead.interactions.length > 0
    ? lead.interactions[0] as { eventType?: unknown } | null
    : null
  return first?.eventType === 'email_solicitation'
}

// ── Mirrors of the server's email-identity + direction rules (2026-09-30) ─────
// Server: agents/leadExclusion.ts (ownerEmailSet · isAutomatedSenderAddress ·
// ownDomains · ownerFullNames) and leads/leadAttribution.ts
// (firstInteractionIsInbound). Kept in step by the shared truth table.

const EMAIL_RE = /[a-z0-9][a-z0-9._%+-]*@[a-z0-9.-]+\.[a-z]{2,}/gi

function emailsIn(v: unknown): string[] {
  const vals = Array.isArray(v) ? v : [v]
  const out: string[] = []
  for (const x of vals) {
    if (typeof x !== 'string') continue
    for (const m of x.match(EMAIL_RE) ?? []) out.push(m.toLowerCase())
  }
  return out
}

function ownerEmailSet(client: LeadExclusionClient): Set<string> {
  return new Set([
    ...emailsIn(client.email), ...emailsIn(client.ownerEmail),
    ...emailsIn(client.businessEmail), ...emailsIn(client.googleWorkspaceEmail),
    ...emailsIn(client.connectedEmails),
  ])
}

const AUTOMATED_LOCAL_RE =
  /^(mailer-?daemon|postmaster|no-?reply|do-?not-?reply|donotreply|microsoftexchange[0-9a-f]*)([+._-][a-z0-9._+-]*)?$/i

export function isAutomatedSenderAddress(email: string | null | undefined): boolean {
  const local = String(email ?? '').toLowerCase().split('@')[0] ?? ''
  return local.length > 0 && AUTOMATED_LOCAL_RE.test(local)
}

const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com', 'outlook.com',
  'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'comcast.net',
  'att.net', 'sbcglobal.net', 'verizon.net', 'protonmail.com', 'proton.me', 'gmx.com',
  'cox.net', 'charter.net', 'earthlink.net',
])
const PLATFORM_HOSTS = [
  'facebook.com', 'instagram.com', 'google.com', 'business.site', 'g.page', 'wixsite.com',
  'squarespace.com', 'godaddysites.com', 'linktr.ee', 'yelp.com', 'weebly.com', 'sendbrook.com',
]

function hostOf(v: unknown): string {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : ''
  if (!s) return ''
  const noScheme = s.replace(/^[a-z]+:\/\//, '')
  return (noScheme.split(/[/?#:]/)[0] ?? '').replace(/^www\./, '')
}

function ownDomains(client: LeadExclusionClient): string[] {
  const out = new Set<string>()
  for (const v of [client.website, client.domainName, client.ownedDomain]) {
    const h = hostOf(v)
    if (h && h.includes('.') && !FREE_MAIL_DOMAINS.has(h)
      && !PLATFORM_HOSTS.some((p) => h === p || h.endsWith(`.${p}`))) out.add(h)
  }
  for (const e of ownerEmailSet(client)) {
    const d = e.split('@')[1] ?? ''
    if (d && !FREE_MAIL_DOMAINS.has(d)) out.add(d)
  }
  return [...out]
}

function normalizePersonName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
}

function ownerFullNames(client: LeadExclusionClient): Set<string> {
  const out = new Set<string>()
  const add = (n: string): void => {
    const k = normalizePersonName(n)
    if (k.includes(' ')) out.add(k)
  }
  const raw = typeof client.ownerName === 'string' ? client.ownerName.trim() : ''
  let last = typeof client.ownerLastName === 'string' ? client.ownerLastName.trim() : ''
  if (raw) {
    add(raw)
    const people = raw.split(/\s*(?:&|\+|\band\b|,|\/)\s*/i).map((p) => p.trim()).filter(Boolean)
    const lastTokens = (people[people.length - 1] ?? '').split(/\s+/)
    if (!last && lastTokens.length >= 2) last = lastTokens[lastTokens.length - 1]
    for (const p of people) {
      add(p)
      if (last && !p.includes(' ')) add(`${p} ${last}`)
    }
  }
  const first = typeof client.ownerFirstName === 'string' ? client.ownerFirstName.trim() : ''
  if (first && last) add(`${first} ${last}`)
  return out
}

const OUTBOUND_FIRST_EVENT_TYPES = new Set([
  'email_sent_by_owner', 'review_request_sent', 'reminder_sent', 'quote_sent', 'customer_import',
])

/** PURE — was the row CREATED by a contact reaching in? Mirrors the server. */
export function firstInteractionIsInbound(interactions: unknown): boolean {
  if (!Array.isArray(interactions) || interactions.length === 0) return true
  const first = interactions[0] as { eventType?: unknown } | null
  const t = typeof first?.eventType === 'string' ? first.eventType : ''
  if (!t) return true
  if (OUTBOUND_FIRST_EVENT_TYPES.has(t)) return false
  return !t.startsWith('nurture_')
}

/**
 * PURE — the NEWNESS axis. Mirrors the server's emailRowIsNewInquiry (2026-10-01):
 * an EMAIL-created row is a new lead only when the interaction that created it
 * carries the classifier's `newInquiry: true`. Fails closed — Susan's "7 new
 * leads" were seven regulars writing about current orders.
 */
export function emailRowIsNewInquiry(lead: { source?: unknown; interactions?: unknown }): boolean {
  const first = Array.isArray(lead.interactions) && lead.interactions.length > 0
    ? lead.interactions[0] as { channel?: unknown; eventType?: unknown; newInquiry?: unknown } | null
    : null
  const firstIsEmail = !!first && (first.channel === 'email'
    || (typeof first.eventType === 'string' && first.eventType.startsWith('email_')))
  if (lead.source !== 'email' && !firstIsEmail) return true
  return first?.newInquiry === true
}

/** Mirrors the server's TEST_CONTACT_NAME_RE (John, 2026-09-27): whole word "test"/"testing". */
export const TEST_CONTACT_NAME_RE = /\btest(ing)?\b/i;

export function isTestContactName(name: unknown): boolean {
  return typeof name === 'string' && TEST_CONTACT_NAME_RE.test(name);
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** PURE. Mirrors the server: 10 digits gain +1, 11 leading 1 gains +, else raw +digits. */
export function normalizeForExclusion(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  return `+${digits}`;
}

/** PURE — did this lead arrive through something we can honestly claim credit for? */
export function isArrivedSource(source: unknown): boolean {
  return typeof source === 'string'
    && (ARRIVED_LEAD_SOURCES as readonly string[]).includes(source);
}

/**
 * PURE — why this contact must not be counted as a lead, or null when it may be.
 *
 * ⚠️ TWO AXES, AND THEY ARE DIFFERENT QUESTIONS. `isArrivedSource` filters HOW a
 * lead arrived; this filters WHO it is. The 09-11 bug was a surface applying one
 * and not the other.
 */
export function leadExclusionReason(
  client: LeadExclusionClient | null | undefined,
  contact: { phone?: string; email?: string; name?: string } | null | undefined,
  extraPlatformNumbers: readonly string[] = [],
): LeadExclusionReason | null {
  if (!client || !contact) return null;

  const phone = str(contact.phone);
  if (phone) {
    const n = normalizeForExclusion(phone);
    const owner = [str(client.phone), str(client.ownerPhone)]
      .filter(Boolean).map(normalizeForExclusion);
    if (owner.includes(n)) return 'owner-own-number';

    // Mirrors the server (2026-10-02, Todd's test call booked as "James").
    const tests = (Array.isArray(client.ownerTestNumbers) ? client.ownerTestNumbers : [])
      .map(str).filter(Boolean).map(normalizeForExclusion);
    if (tests.includes(n)) return 'owner-test-number';

    const platform = [str(client.avaPhoneNumber), ...extraPlatformNumbers.map(str)]
      .filter(Boolean).map(normalizeForExclusion);
    if (platform.includes(n)) return 'platform-number';

    const provisioned = [
      str(client.businessPhoneNumber),
      str(client.baseTexting?.phoneNumber),
    ].filter(Boolean).map(normalizeForExclusion);
    if (provisioned.includes(n)) return 'client-provisioned-number';
  }

  const email = str(contact.email).toLowerCase();
  if (email) {
    if (ownerEmailSet(client).has(email)) return 'owner-own-email';
    if (isAutomatedSenderAddress(email)) return 'automated-sender';
    const domain = email.split('@')[1] ?? '';
    const own = ownDomains(client);
    if (domain && own.some((o) => domain === o || domain.endsWith(`.${o}`))) return 'client-own-domain';
  }

  const name = normalizePersonName(str(contact.name));
  if (name.includes(' ') && ownerFullNames(client).has(name)) return 'owner-own-name';

  return null;
}

/** PURE — both axes, the question every client-facing lead count must ask. */
export function isCountableLead(
  lead: LeadRowForAttribution | null | undefined,
  client: LeadExclusionClient | null | undefined,
  extraPlatformNumbers: readonly string[] = [],
): boolean {
  if (!lead) return false;
  if (isSolicitationRow(lead)) return false;
  if (!isArrivedSource(lead.source)) return false;
  if (!firstInteractionIsInbound(lead.interactions)) return false;
  if (!emailRowIsNewInquiry(lead)) return false;
  const phone = typeof lead.phone === 'string' ? lead.phone
    : typeof lead.phoneNumber === 'string' ? lead.phoneNumber : '';
  const email = typeof lead.email === 'string' ? lead.email : '';
  const name = typeof lead.name === 'string' ? lead.name : '';
  if (leadExclusionReason(client, { phone, email, name }, extraPlatformNumbers) !== null) return false;
  return !isTestContactName(lead.name);
}

/**
 * PURE — the WHO axis alone: is this row a real outside PERSON (not the owner, not us, not a
 * self-labelled test, not someone selling to the business)? Mirrors the server's
 * isOutsidePerson (leads/leadAttribution.ts). The Leads list hides a row that fails it on the
 * test axis and every count skips it (Control Center audit E8, 2026-10-28: "TEST Rudy cutover
 * check 2" in CPA Jake's customer list). Held to the server by the fixture's `outsideCases`.
 */
export function isOutsidePerson(
  lead: LeadRowForAttribution | null | undefined,
  client: LeadExclusionClient | null | undefined,
  extraPlatformNumbers: readonly string[] = [],
): boolean {
  if (!lead) return false
  const m = lead.notACustomer as { reason?: unknown } | null | undefined
  if (m && typeof m === 'object' && typeof m.reason === 'string' && m.reason.length > 0) return false
  const phone = typeof lead.phone === 'string' ? lead.phone
    : typeof lead.phoneNumber === 'string' ? lead.phoneNumber : ''
  const email = typeof lead.email === 'string' ? lead.email : ''
  const name = typeof lead.name === 'string' ? lead.name : ''
  if (leadExclusionReason(client, { phone, email, name }, extraPlatformNumbers) !== null) return false
  return !isTestContactName(lead.name)
}

export interface LeadMetricsRow extends LeadRowForAttribution {
  status?: unknown;
}

export interface LeadMetricsResult {
  /** Leads we can honestly present as theirs. */
  total: number;
  /** Of those, how many are booked. */
  booked: number;
  bySource: Record<string, number>;
  byStatus: Record<string, number>;
  /**
   * Rows deliberately NOT counted, with the reason.
   *
   * ⚠️ Returned rather than discarded so a surface can EXPLAIN a number instead of
   * a client wondering why their dashboard says 0 when they can see two rows. An
   * exclusion must leave evidence, never silence — the server's
   * `explainLeadExclusion` exists for the same reason.
   */
  excluded: Array<{ reason: LeadExclusionReason | 'source-not-arrived'; row: LeadMetricsRow }>;
  /**
   * The rows that COUNTED, in input order.
   *
   * ⚠️ ADDED 2026-09-13 because a count is not enough. The Control Center
   * Dashboard used the exclusion-aware TOTAL for its metrics and then rendered
   * its "Recent Leads" table straight off the raw array — so both paying clients
   * were shown a table whose rows were the owner's own mobile and OUR platform
   * number, labelled as leads. Handing back the rows means a consumer never has
   * to re-derive the filter and get a different answer than the number beside it.
   */
  included: LeadMetricsRow[];
}

/**
 * PURE — the ONE client-facing lead derivation.
 *
 * Extracted from `useClientData`'s metrics memo so the arithmetic behind a number
 * a client SEES is a named function with its own tests, rather than a loop inside
 * a hook. That is the whole point of auditing derivations: a number nobody can
 * test is a number nobody can trust.
 */
export function computeLeadMetrics(
  leads: readonly LeadMetricsRow[],
  client: LeadExclusionClient | null | undefined,
  extraPlatformNumbers: readonly string[] = [],
): LeadMetricsResult {
  const bySource: Record<string, number> = {};
  const byStatus: Record<string, number> = {};
  const excluded: LeadMetricsResult['excluded'] = [];
  const included: LeadMetricsRow[] = [];
  let booked = 0;
  let total = 0;

  for (const lead of leads ?? []) {
    if (!lead) continue;
    if (isSolicitationRow(lead)) {
      excluded.push({ reason: 'solicitation', row: lead });
      continue;
    }
    if (!isArrivedSource(lead.source)) {
      excluded.push({ reason: 'source-not-arrived', row: lead });
      continue;
    }
    const phone = typeof lead.phone === 'string' ? lead.phone
      : typeof lead.phoneNumber === 'string' ? lead.phoneNumber : '';
    const email = typeof lead.email === 'string' ? lead.email : '';
    const name = typeof lead.name === 'string' ? lead.name : '';
    const reason = (!firstInteractionIsInbound(lead.interactions) ? 'outbound-first' as const : null)
      ?? (!emailRowIsNewInquiry(lead) ? 'email-not-new-inquiry' as const : null)
      ?? leadExclusionReason(client, { phone, email, name }, extraPlatformNumbers)
      ?? (isTestContactName(lead.name) ? 'test-contact' as const : null);
    if (reason) {
      excluded.push({ reason, row: lead });
      continue;
    }
    total += 1;
    included.push(lead);
    const source = typeof lead.source === 'string' ? lead.source : 'unknown';
    const status = typeof lead.status === 'string' ? lead.status : 'unknown';
    bySource[source] = (bySource[source] ?? 0) + 1;
    byStatus[status] = (byStatus[status] ?? 0) + 1;
    if (status === 'booked') booked += 1;
  }

  // `included.length === total` by construction — asserted in the test, because
  // two numbers that must agree are exactly where this codebase drifts.
  return { total, booked, bySource, byStatus, excluded, included };
}
